import { useCallback, useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { useToastContext } from '@/contexts/toastContext';
import { FormErrors } from '@/types';
import { CompetencyScore, TechnicalValidationCompetency, User } from '@/types/api';
import {
  TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
  getInputNumberedValue,
  TECHNICAL_VALIDATION_COMPETENCIES,
} from '@/utils/interviews';
import TechnicalValidationReviewComponent from './TechnicalValidationReview.component';
import { CustomReviewType } from '../InterviewReviewsManager/InterviewReviewsManager.component';

export type TechnicalValidationReviewProps = {
  index: number;
  id?: string;
  overviewNote?: string;
  competencies: TechnicalValidationCompetency[];
  reviewer?: User;
  isActionLoading: boolean;
  reviewedAt?: string;
  onCancel: (TechnicalValidationReviewIndex: number) => void;
  onSave: (index: number, newReview: CustomReviewType) => Promise<boolean>;
  onDelete: (TechnicalValidationReviewId: string) => Promise<boolean>;
};

type EditingReview = {
  id: string;
  reviewer: User | undefined;
  reviewedAt: Date | undefined;
  overviewNote: string;
  prevOverviewNote: string;
  competencies: TechnicalValidationCompetency[];
  prevCompetencies: TechnicalValidationCompetency[];
  isEditing: boolean;
  isExpanded: boolean;
};

export default function TechnicalValidationReview({
  isActionLoading,
  index,
  id = '',
  competencies,
  overviewNote = '',
  reviewer,
  reviewedAt,
  onSave,
  onDelete,
  onCancel,
}: TechnicalValidationReviewProps): JSX.Element {
  const [review, setReview] = useState<EditingReview>(() => {
    return {
      id,
      reviewer,
      reviewedAt: reviewedAt ? new Date(reviewedAt) : undefined,
      competencies,
      prevCompetencies: cloneDeep(competencies),
      overviewNote,
      prevOverviewNote: overviewNote,
      isEditing: !id,
      isExpanded: !id,
    };
  });
  const toast = useToastContext();

  useEffect(() => {
    setReview((prev) => ({
      ...prev,
      id,
      overviewNote,
      reviewedAt: reviewedAt ? new Date(reviewedAt) : undefined,
      competencies,
      isEditing: !id,
      isExpanded: !id,
    }));
  }, [id, overviewNote, reviewedAt, competencies]);

  const getCompetencyInputsTotalScore = useCallback((inputs: CompetencyScore[]): number => {
    const competencyInputsScore = inputs.reduce((inputsAccumulator: number, input) => {
      return inputsAccumulator + (input.scoreValue || 0);
    }, 0);

    return competencyInputsScore;
  }, []);

  const reviewerScore = useMemo(
    () =>
      review.competencies.reduce(
        (accumulator: number, competency) =>
          accumulator + getCompetencyInputsTotalScore(competency.scores) * competency.weight, // Apply weight when calculating total
        0
      ),
    [getCompetencyInputsTotalScore, review]
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const onChangeScoreInput = (value: string, competencyName: string, inputName: string): void => {
    setReview((prev) => {
      const updatedReview = prev;

      const competencyIndex = updatedReview.competencies.findIndex(
        (competency) => competency.name === competencyName
      );
      const inputIndex = updatedReview.competencies[competencyIndex].scores.findIndex(
        (input) => input.name === inputName
      );

      updatedReview.competencies[competencyIndex].scores[inputIndex].value = value;

      updatedReview.competencies[competencyIndex].scores[inputIndex].scoreValue =
        getInputNumberedValue(
          value,
          updatedReview.competencies[competencyIndex].scores[inputIndex].maxScoreValue
        );

      return { ...updatedReview };
    });
  };

  const onChangeAdditionalInfoInput = (value: string, competencyName: string): void => {
    setReview((prev) => {
      const updatedReview = prev;

      const competencyIndex = updatedReview.competencies.findIndex(
        (competency) => competency.name === competencyName
      );

      updatedReview.competencies[competencyIndex].additionalInfo = value;

      return { ...updatedReview };
    });
  };

  const onChangeOverviewNote = (event: any): void => {
    const { value } = event.target;

    setReview((prev) => ({
      ...prev,
      overviewNote: value,
    }));
  };

  const onToggleIsEditing = (): void => {
    setReview((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
    }));
  };

  const onCancelEdition = (triggerCallback = true, triggerToggleEdit = true): void => {
    const currentId = review.id;

    setReview((prev) => ({
      ...prev,
      overviewNote: prev.prevOverviewNote,
      competencies: cloneDeep(prev.prevCompetencies),
    }));

    if (triggerCallback && !reviewedAt) {
      onCancel(index);
    }

    if (triggerToggleEdit && !!currentId) {
      onToggleIsEditing();
    }
  };

  const onExpandView = (): void => {
    setReview((prev) => ({
      ...prev,
      isExpanded: true,
    }));
  };

  const onCollapseView = (): void => {
    setReview((prev) => ({
      ...prev,
      isExpanded: false,
    }));

    onCancelEdition(true, false);
  };

  const getFormattedReview = (reviewToFormat: EditingReview): CustomReviewType => {
    return {
      ...omit(reviewToFormat, [
        'reviewer',
        'reviewedAt',
        'isEditing',
        'prevOverviewNote',
        'prevCompetencies',
      ]),
      user: reviewToFormat.reviewer,
      note: reviewToFormat.overviewNote,
    };
  };

  const findSomeEmptyRequiredScore = (
    competenciesToVerify: TechnicalValidationCompetency[]
  ): string | undefined => {
    for (let i = 0; i < competenciesToVerify.length; i += 1) {
      const emptyScore = competenciesToVerify[i].scores.find((input) => {
        const isBooleanInput = (input.options as string[])?.includes('Yes');
        return !isBooleanInput && input.value === TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER;
      })?.name;

      if (emptyScore) return emptyScore;
    }

    return undefined;
  };

  const findSomeEmptyRequiredField = (): string | undefined => {
    const noneInputIsEmpty = findSomeEmptyRequiredScore(review.competencies);
    const overviewNoteIsNotEmpty = !review.overviewNote;

    return noneInputIsEmpty || (overviewNoteIsNotEmpty ? 'note' : undefined);
  };

  const emptyRequiredField = findSomeEmptyRequiredField();
  const canSave = !emptyRequiredField;
  const [dirty, setDirty] = useState<boolean>(id !== '');

  const onSaveReview = async (): Promise<void> => {
    setDirty(true);

    if (!canSave) {
      toast.showErrorFeedback(FormErrors.MISSING_REQUIRED_FIELDS);

      if (emptyRequiredField) {
        document.getElementById(emptyRequiredField)?.scrollIntoView({
          behavior: 'smooth',
        });
      }

      return;
    }

    const newReview = {
      ...review,
      prevOverviewNote: review.overviewNote,
      prevCompetencies: cloneDeep(review.competencies),
      reviewedAt: new Date(),
      reviewer,
    };

    const isSaved = await onSave(index, getFormattedReview(newReview));

    if (isSaved) {
      setReview((prev) => ({
        ...prev,
        ...newReview,
        isExpanded: false,
        isEditing: false,
      }));
    } else {
      onCancelEdition(false, false);
    }
  };

  const onToggleDeleteModal = (event?: any): void => {
    event?.stopPropagation();
    setIsDeleteModalOpen((prev) => !prev);
  };

  const resetReview = (): void => {
    setReview({
      id: '',
      reviewer: undefined,
      reviewedAt: undefined,
      competencies: cloneDeep(TECHNICAL_VALIDATION_COMPETENCIES),
      prevCompetencies: cloneDeep(TECHNICAL_VALIDATION_COMPETENCIES),
      overviewNote: '',
      prevOverviewNote: '',
      isEditing: false,
      isExpanded: false,
    });
  };

  const onDeleteReview = async (): Promise<void> => {
    onToggleDeleteModal();

    const isDeleted = await onDelete(review.id);

    if (isDeleted) {
      resetReview();
    }
  };

  return (
    <TechnicalValidationReviewComponent
      competencies={review.competencies}
      reviewerScore={reviewerScore}
      reviewer={review.reviewer}
      reviewedAt={review.reviewedAt}
      overviewNote={review.overviewNote}
      isEditing={review.isEditing}
      isExpanded={review.isExpanded}
      onChangeScoreInput={onChangeScoreInput}
      onChangeAdditionalInfoInput={onChangeAdditionalInfoInput}
      onChangeOverviewNote={onChangeOverviewNote}
      isActionLoading={isActionLoading}
      onToggleIsEditing={onToggleIsEditing}
      onExpandView={onExpandView}
      onCollapseView={onCollapseView}
      onSaveReview={onSaveReview}
      onDeleteReview={onDeleteReview}
      isDeleteModalOpen={isDeleteModalOpen}
      onToggleDeleteModal={onToggleDeleteModal}
      onCancelEdition={onCancelEdition}
      isDirty={dirty}
    />
  );
}
